import React, { useState, useEffect } from 'react';
import {
  Users, CheckCircle2, AlertCircle, TrendingUp,
  Instagram, Facebook, Linkedin, Youtube,
  ArrowUpRight, ArrowDownRight, Globe,
  MoreVertical, Calendar, Activity, RefreshCw, DollarSign
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, AreaChart, Area,
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import API from '@/api/api01';
import { toast } from 'sonner';
import { exportToCSV } from '@/utils/exportUtils';

// PLATFORM_COLORS moved to local scope or reused from stats if needed
const PLATFORM_COLORS: any = {
  Instagram: '#E1306C',
  Facebook: '#1877F2',
  LinkedIn: '#0A66C2',
  YouTube: '#FF0000',
  Twitter: '#1DA1F2',
  TikTok: '#000000'
};

interface SocialDashboardProps {
  onTabChange?: (tab: string) => void;
}

export default function SocialDashboard({ onTabChange }: SocialDashboardProps) {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Dynamic data states derived from backend stats
  const [recentLeads, setRecentLeads] = useState<any[]>([]);
  const [platformData, setPlatformData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [radarData, setRadarData] = useState<any[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const res = await API.get('/superadmin/social-stats/');
        const data = res.data;
        setStats(data);

        // Populate specific chart states
        if (data.recent_leads) setRecentLeads(data.recent_leads);
        if (data.platform_data) setPlatformData(data.platform_data);
        if (data.trend_data) setTrendData(data.trend_data);
        if (data.radar_data) setRadarData(data.radar_data);

        if (data.recent_leads?.length > 0) {
          toast.success("Social dashboard updated");
        } else {
          // Silent if no records, or handle gracefully
        }
      } catch (err) {
        toast.error("Failed to load dashboard stats");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleExportReport = () => {
    if (!stats) {
      toast.error("Dashboard data not loaded");
      return;
    }

    // Export a summary of the current dashboard state
    const reportData = [
      { Category: 'Total Leads', Value: stats.total_leads },
      { Category: 'Verified Leads', Value: stats.verified_leads },
      { Category: 'Active Campaigns', Value: stats.campaigns_count || 0 },
      { Category: 'Total Value', Value: `$${stats.total_value || 0}` },
      { Category: 'Conversion Rate', Value: stats ? `${((stats.converted_leads / stats.total_leads) * 100 || 0).toFixed(1)}%` : '0%' }
    ];

    // Add platform distribution if available
    if (stats.platform_distribution) {
      stats.platform_distribution.forEach((p: any) => {
        reportData.push({ Category: `Leads from ${p.platform}`, Value: p.count });
      });
    }

    exportToCSV(reportData, `social_performance_report_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Dashboard report exported");
  };

  const kpiData = [
    {
      label: 'Conversion Rate',
      value: stats ? `${((stats.converted_leads / stats.total_leads) * 100 || 0).toFixed(1)}%` : '0%',
      change: stats?.conv_change || '+0%',
      icon: <TrendingUp size={20} />,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      label: 'Verified Rate',
      value: stats ? `${((stats.verified_leads / stats.total_leads) * 100 || 0).toFixed(1)}%` : '0%',
      change: stats?.verified_change || '+0%',
      icon: <CheckCircle2 size={20} />,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50'
    },
    {
      label: 'Lead Growth',
      value: stats?.total_leads || 0,
      change: stats?.lead_change || '+0%',
      icon: <Users size={20} />,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      label: 'Total Value',
      value: stats ? `$${(stats.total_value / 1000).toFixed(1)}k` : '$0',
      change: stats?.value_change || '+0%',
      icon: <DollarSign size={20} />,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50'
    },
  ];

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
            Social Lead Dashboard
          </h1>

          <p className="text-[13px] font-medium text-slate-500 dark:text-slate-400 mt-2">Real-time performance of your social media advertising campaigns.</p>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => toast.info("Filter applied: Last 7 Days")} className="rounded-xl border-white/10 dark:bg-background/50 shadow-sm">
            <Calendar className="mr-2 h-4 w-4" />
            Last 7 Days
          </Button>
          <Button onClick={handleExportReport} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 px-6">
            <span className="text-sm font-bold">Export Report</span>
          </Button>

        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, i) => (
          <Card key={i} className="border-border shadow-sm rounded-3xl overflow-hidden group hover:shadow-xl transition-all duration-300 relative bg-card dark:backdrop-blur-xl">
            {loading && <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-10 transition-opacity">
              <RefreshCw className="animate-spin text-purple-600" />
            </div>}
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={`p-3 rounded-2xl ${kpi.bg}`}>
                  {React.cloneElement(kpi.icon as React.ReactElement, { className: kpi.color })}
                </div>
                <Badge variant="secondary" className={`bg-transparent ${kpi.change.startsWith('+') ? 'text-emerald-500' : 'text-red-500'} font-medium`}>
                  {kpi.change.startsWith('+') ? <ArrowUpRight size={14} className="mr-1" /> : <ArrowDownRight size={14} className="mr-1" />}
                  {kpi.change}
                </Badge>
              </div>
              <div className="mt-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">{kpi.label}</p>
                <h3 className="text-2xl font-bold mt-1 tracking-tight">{kpi.value}</h3>
              </div>

            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Chart */}
        <Card className="lg:col-span-2 border-border shadow-sm rounded-2xl bg-card dark:backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">Lead Acquisition Trend</CardTitle>
            <CardDescription className="text-[12px]">Daily volume of leads across all social platforms</CardDescription>
          </CardHeader>

          <CardContent className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(100,116,139,0.15)" />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  cursor={{ stroke: '#8b5cf6', strokeWidth: 2 }}
                />
                <Area type="monotone" dataKey="leads" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorLeads)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Platform Distribution */}
        <Card className="border-border shadow-sm rounded-2xl bg-card dark:backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">Lead Quality Matrix</CardTitle>
            <CardDescription className="text-[12px]">Multi-dimensional performance overview</CardDescription>
          </CardHeader>

          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                <PolarGrid stroke="rgba(100,116,139,0.2)" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} hide />
                <Radar name="Current" dataKey="A" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} />
                <Radar name="Target" dataKey="B" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <Card className="border-border shadow-sm rounded-2xl bg-card dark:backdrop-blur-xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2 relative z-10">
            <div>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">Recent Activity</CardTitle>
              <CardDescription className="text-[12px]">Latest leads captured in the last 24 hours</CardDescription>
            </div>

            <Button variant="ghost" size="sm" onClick={() => onTabChange?.('lead-manager')} className="text-purple-600 relative z-20 hover:bg-purple-50">View All</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {recentLeads.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                  <TrendingUp className="h-12 w-12 mb-4 opacity-20" />
                  <p className="text-lg font-medium">No records found</p>
                  <p className="text-sm text-center">No social leads captured in the last 24 hours</p>
                </div>
              ) : (
                recentLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between group">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500">
                        {lead.name.split(' ').map((n: string) => n[0]).join('')}
                      </div>
                      <div>
                        <p className="text-[13px] font-bold text-slate-900 dark:text-white leading-tight">{lead.name}</p>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <span className="flex items-center gap-1">
                            {lead.platform === 'Instagram' && <Instagram size={12} className="text-[#E1306C]" />}
                            {lead.platform === 'Facebook' && <Facebook size={12} className="text-[#1877F2]" />}
                            {lead.platform === 'LinkedIn' && <Linkedin size={12} className="text-[#0A66C2]" />}
                            {lead.platform === 'YouTube' && <Youtube size={12} className="text-[#FF0000]" />}
                            {lead.platform}
                          </span>
                          <span>•</span>
                          <span>{lead.campaign}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant="secondary" className="text-[10px] font-bold uppercase tracking-widest py-0 h-4 bg-slate-100 dark:bg-white/5 text-slate-500 border-none">{lead.status}</Badge>
                      <span className="text-[10px] text-muted-foreground font-medium">{lead.time}</span>
                    </div>

                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Campaign Performance */}
        <Card className="border-border shadow-sm rounded-2xl bg-card dark:backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground/80">Top Performing Campaigns</CardTitle>
            <CardDescription className="text-[12px]">Conversion performance by marketing campaign</CardDescription>
          </CardHeader>

          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={platformData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(100,116,139,0.15)" />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} width={80} />
                <Tooltip
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={24}>
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
